'use client';

import type { ElementRef, ForwardedRef, PropsWithChildren, ReactNode } from 'react';
import { forwardRef } from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { Label } from '@radix-ui/react-label';
import clsx from 'clsx';

import { ChevronDown, ChevronUp } from '../Icons';

import styles from './Select.module.scss';

type SelectProps = {
  /**
   * The state to represent if the input should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string or Transifex <T /> component to use for the label of the Select.
   */
  label?: ReactNode;

  /**
   * The name of the Select to use as htmlFor value for the Select's label and the id value of the Select for accessibility purposes.
   */
  name: string;

  onValueChange?: (selectedValue: string) => void;

  /**
   * The translated string component to use when no item has been selected from the Select Trigger; also used for SelectTrigger aria-label so cannot be <T /> component from Transifex.
   */
  placeholder?: string;

  /**
   * Selected value (ie value from SelectHTMLAttributes)
   */
  selectedValue?: string;

  /**
   * To supress the display of the label
   */
  hideLabel?: boolean;

  /**
   * The boolean state to determine if the radio dots should be displayed in the Select Items.
   */
  displayRadioDots?: boolean;

  /**
   * The translated string or Transifex <T /> component to use for the error text of the Select.
   */
  errorText?: ReactNode;

  /**
   * The translated string or Transifex <T /> component to use for the supporting text of the Select.
   */
  supportingText?: ReactNode;

  /**
   * The boolean state to determine if the select is rounded or square shape.
   */
  isRounded?: boolean;

  /**
   * There are multiple dropdowns stylings so helpful defaults
   */
  variant?: 'primary' | 'primary-outlined' | 'tonal' | 'tonal-outlined' | 'book-creator' | 'signup-flow';

  /**
   * If supplied, the text when the select is closed will be rendered by this method
   * @param value `value` field from the selected option
   */
  renderLabel?(value: string): ReactNode;

  /**
   * Object containing data attributes to be applied to the Select.
   * Keys will be converted to data-* attributes (e.g., { recurly: "country" } becomes data-recurly="country")
   */
  data?: Record<string, string>;

  /**
   * When true, an asterisk is added to the label.
   */
  required?: boolean;
};

type NonGroupedSelectProps = SelectProps & {
  /**
   * The array of options to render as Select Items within the Select.
   */
  options: {
    /**
     * The translated string or Transifex <T /> component to use for the label of the Select Item.
     */
    label?: ReactNode;

    /**
     * The value of the Select Item.
     */
    value: string;
  }[];
};

type GroupedSelectProps = SelectProps & {
  /**
   * The array of options to render as Select Items within the Select.
   */
  options: {
    /**
     * The translated string or Transifex <T /> component to use for the label for the group of the Select Items.
     */
    groupLabel: ReactNode;

    /**
     * The id of the group
     */
    groupId: string | number;

    /**
     * an array of select item objects for this group
     */
    items: {
      /**
       * The translated string or Transifex <T /> component to use for the label of the Select Item.
       */
      label?: ReactNode;

      /**
       * The value of the Select Item.
       */
      value: string;
    }[];
  }[];
};

const styling = (variant: string, isRounded: boolean) => {
  return {
    [styles.primary]: variant === 'primary',
    [styles['primary-outlined']]: variant === 'primary-outlined',
    [styles.tonal]: variant === 'tonal',
    [styles['tonal-outlined']]: variant === 'tonal-outlined',
    [styles['book-creator']]: variant === 'book-creator',
    [styles['signup-flow']]: variant === 'signup-flow',
    [styles.square]: !isRounded,
    [styles.rounded]: isRounded,
  };
};

export const Select = ({
  isDisabled,
  label,
  name,
  onValueChange,
  placeholder,
  selectedValue,
  displayRadioDots = true,
  errorText,
  supportingText,
  variant = 'book-creator',
  isRounded = false,
  options,
  required,
  ...selectProps
}: NonGroupedSelectProps) => {
  return (
    <SelectCommon
      isDisabled={isDisabled}
      label={label}
      name={name}
      onValueChange={onValueChange}
      placeholder={placeholder}
      selectedValue={selectedValue}
      displayRadioDots={displayRadioDots}
      errorText={errorText}
      supportingText={supportingText}
      variant={variant}
      isRounded={isRounded}
      required={required}
      {...selectProps}
    >
      {options.map(({ label, value }, index) => {
        const final = index === options.length - 1;
        return (
          <SelectPrimitive.Item
            className={clsx(
              styles.item,
              { [styles.final]: final, [styles.first]: index === 0 },
              styling(variant, isRounded),
            )}
            disabled={isDisabled}
            key={`${value}-${index}`}
            value={value}
          >
            <SelectPrimitive.ItemText>
              <div className={styles.option}>
                {displayRadioDots && (
                  <div
                    className={clsx(styles.radio, styling(variant, isRounded), {
                      [styles.selected]: value === selectedValue,
                    })}
                  />
                )}
                <div className={styles.text}>{label || value}</div>
              </div>
            </SelectPrimitive.ItemText>
          </SelectPrimitive.Item>
        );
      })}
    </SelectCommon>
  );
};

export const SelectGrouped = ({
  isDisabled,
  label,
  name,
  onValueChange,
  placeholder,
  selectedValue,
  displayRadioDots = true,
  errorText,
  supportingText,
  variant = 'primary-outlined',
  isRounded = true,
  options,
  required,
  ...selectProps
}: GroupedSelectProps) => {
  return (
    <SelectCommon
      isDisabled={isDisabled}
      label={label}
      name={name}
      onValueChange={onValueChange}
      placeholder={placeholder}
      selectedValue={selectedValue}
      displayRadioDots={displayRadioDots}
      errorText={errorText}
      supportingText={supportingText}
      variant={variant}
      isRounded={isRounded}
      required={required}
      {...selectProps}
    >
      {options.map(({ groupLabel, groupId, items }, index) => {
        // used for styling
        const finalGroup = index === options.length - 1;
        return (
          <SelectPrimitive.Group>
            <SelectPrimitive.Label className={clsx(styles.group)}>{groupLabel}</SelectPrimitive.Label>
            {items.map(({ label, value }, index) => {
              const final = index === items.length - 1 && finalGroup;
              return (
                <SelectPrimitive.Item
                  className={clsx(styles.item, { [styles.final]: final }, styling(variant, isRounded))}
                  disabled={isDisabled}
                  key={`${groupId}-${value}-${index}`}
                  value={value}
                >
                  <SelectPrimitive.ItemText>
                    <div className={styles.option}>
                      {displayRadioDots && (
                        <div
                          className={clsx(styles.radio, styling(variant, isRounded), {
                            [styles.selected]: value === selectedValue,
                          })}
                        />
                      )}
                      <div className={styles.text}>{label || value}</div>
                    </div>
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              );
            })}
          </SelectPrimitive.Group>
        );
      })}
    </SelectCommon>
  );
};

const SelectCommon = forwardRef(
  (
    {
      isDisabled,
      label,
      name,
      onValueChange,
      placeholder,
      selectedValue,
      displayRadioDots = true,
      errorText,
      supportingText,
      variant = 'primary-outlined',
      isRounded = true,
      children,
      renderLabel,
      hideLabel = false,
      data,
      required,
    }: PropsWithChildren<SelectProps>,
    ref: ForwardedRef<ElementRef<typeof SelectPrimitive.Trigger>>,
  ) => {
    // Convert data object to data-* attributes
    const dataAttributes: Record<string, string> = {};
    if (data) {
      Object.keys(data).forEach((key) => {
        dataAttributes[`data-${key}`] = data[key];
      });
    }

    return (
      <div
        className={clsx(styles.container, {
          [styles.disabled]: isDisabled,
          [styles['label-hidden']]: hideLabel,
          [styles['signup-flow']]: variant === 'signup-flow',
        })}
      >
        <Label className={clsx(styles.label, styling(variant, isRounded))} htmlFor={name}>
          {label}
          {required && <span className={styles.asterisk}>*</span>}
        </Label>
        <SelectPrimitive.Root value={selectedValue} onValueChange={onValueChange} {...dataAttributes}>
          {supportingText ? (
            <div className={clsx(styles['sub-text'], styling(variant, isRounded))}>{supportingText}</div>
          ) : null}
          <SelectPrimitive.Trigger
            aria-label={selectedValue || placeholder}
            className={clsx(
              styles.trigger,
              styling(variant, isRounded),
              { [styles.disabled]: isDisabled },
              { [styles.error]: errorText },
            )}
            disabled={isDisabled}
            id={name}
            ref={ref}
          >
            <SelectPrimitive.Value
              placeholder={
                <div className={styles.option}>
                  {displayRadioDots && <div className={clsx(styles.radio, styling(variant, isRounded))} />}
                  <div className={styles.text}>{placeholder}</div>
                </div>
              }
            >
              {renderLabel && selectedValue !== undefined ? (
                <div className={styles.option}>
                  {displayRadioDots && <div className={clsx(styles.radio, styling(variant, isRounded))} />}
                  <div className={styles.text}>{renderLabel(selectedValue)}</div>
                </div>
              ) : undefined}
            </SelectPrimitive.Value>
            <SelectPrimitive.Icon className={clsx(styles.icon, styles['trigger-icon'])}>
              <ChevronDown />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              avoidCollisions={false}
              className={clsx(styles.content, styling(variant, isRounded))}
              position="popper"
              side="bottom"
            >
              <SelectPrimitive.ScrollUpButton className={styles.scroll}>
                <ChevronUp className={styles.icon} />
              </SelectPrimitive.ScrollUpButton>

              <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>

              <SelectPrimitive.ScrollDownButton className={styles.scroll}>
                <ChevronDown className={styles.icon} />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {errorText ? (
          <div className={clsx(styles['sub-text'], styles.error, styling(variant, isRounded))}>{errorText}</div>
        ) : null}
      </div>
    );
  },
);
